import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getMe, logout } from './store/authSlice';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import MFAVerify from './pages/MFAVerify';
import SsoLanding from './pages/SsoLanding';
import SsoAuthCallback from './pages/SsoAuthCallback';

// Lazy-loaded pages (code splitting)
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const AssetInventory = React.lazy(() => import('./pages/AssetInventory'));
const AssetDetail = React.lazy(() => import('./pages/AssetDetail'));
const Findings = React.lazy(() => import('./pages/Findings'));
const IAMSecurity = React.lazy(() => import('./pages/IAMSecurity'));
const IAMEntityDetail = React.lazy(() => import('./pages/IAMEntityDetail'));
const NetworkSecurity = React.lazy(() => import('./pages/NetworkSecurity'));
const NetworkTopology = React.lazy(() => import('./pages/NetworkTopology'));
const Compliance = React.lazy(() => import('./pages/Compliance'));
const ComplianceDetail = React.lazy(() => import('./pages/ComplianceDetail'));
const CloudAccounts = React.lazy(() => import('./pages/CloudAccounts'));
const CloudAccountSetup = React.lazy(() => import('./pages/CloudAccountSetup'));
const CloudAccountDetail = React.lazy(() => import('./pages/CloudAccountDetail'));
const ScanHistory = React.lazy(() => import('./pages/ScanHistory'));
const Settings = React.lazy(() => import('./pages/Settings'));
const CloudReports = React.lazy(() => import('./pages/CloudReports'));

function PrivateRoute({ children }) {
  const { isAuthenticated } = useSelector((state) => state.auth);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function App() {
  const dispatch = useDispatch();
  const { token, isAuthenticated } = useSelector((state) => state.auth);

  // Validate saved token on app startup
  useEffect(() => {
    if (token) {
      dispatch(getMe()).unwrap().catch(() => {
        dispatch(logout());
      });
    }
  }, [dispatch, token]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/mfa-verify" element={<MFAVerify />} />
      <Route path="/sso" element={<SsoLanding />} />
      <Route path="/auth/callback" element={<SsoAuthCallback />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="assets" element={<AssetInventory />} />
        <Route path="assets/:id" element={<AssetDetail />} />
        <Route path="findings" element={<Findings />} />
        <Route path="iam" element={<IAMSecurity />} />
        <Route path="iam/:id" element={<IAMEntityDetail />} />
        <Route path="network" element={<NetworkSecurity />} />
        <Route path="network/topology" element={<NetworkTopology />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="compliance/:framework" element={<ComplianceDetail />} />
        <Route path="accounts" element={<CloudAccounts />} />
        <Route path="accounts/new" element={<CloudAccountSetup />} />
        <Route path="accounts/:id" element={<CloudAccountDetail />} />
        <Route path="scans" element={<ScanHistory />} />
        <Route path="settings" element={<Settings />} />
        <Route path="reports" element={<CloudReports />} />
      </Route>
      <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />
    </Routes>
  );
}

export default App;
